import React, { useContext, useEffect, useRef, useState } from "react";

import { BasicTable, RegularText, TextTableHeader } from "shared/components";
import pl from "shared/messages/pl";
import styles from "./view360ReportTable.module.scss";
import { V360ReportData, V360ReportDataSeries } from "view360Card/models";
import { constants } from "shared/constants";
import { formatNumber } from "utils/formattingUtils";
import { ArrowLeftIcon, ArrowRightIcon, Flex, HandIcon, Tooltip } from "@fluentui/react-northstar";
import orderBy from "lodash/orderBy";
import { MsTeamsContext } from "msTeams/store";
import { IThemeTeamsClient } from "msTeamsUI/lib/withTheme";

export interface View360ReportTableProps {
    reportData?: V360ReportData;
}

export const View360ReportTable: React.FC<View360ReportTableProps> = (props) => {
    const [headerItems, setHeaderItems] = useState<any[]>([]);
    const [rows, setRows] = useState<any[]>([]);
    const [showHint, setShowHint] = useState(false);
    const tableContainerRef = useRef(null);
    const msTeamsContext = useContext(MsTeamsContext);

    useEffect(() => {
        setHeaderItems(getHeaderItems());
        setRows(getRows());
    }, [props.reportData]);

    useEffect(() => {
        if (!msTeamsContext.isMobileClient) {
            return;
        }

        const clientWidth = (tableContainerRef?.current as any)?.clientWidth;
        const scrollWidth = (tableContainerRef?.current as any)?.scrollWidth;

        if (clientWidth && scrollWidth && scrollWidth > clientWidth) {
            setShowHint(true);
        }
    }, [rows]);

    const formatValue = (value: any, unitType: string) => {
        if (value === undefined || value === null) {
            return constants.noValue;
        }

        switch (unitType) {
            case constants.unitTypes.numeric:
            case constants.unitTypes.number:
                if (typeof value !== "number") {
                    return value;
                }
                return formatNumber(Math.round(value));
            case constants.unitTypes.percentage:
                return `${formatNumber(value)}${constants.percent}`;
            default:
                return value;
        }
    };

    const getRowsForSeries = (series: V360ReportDataSeries) => {
        const result: any[] = [
            {
                key: `${series.name}-${series.code}-${series.year}-name`,
                content: (
                    <Tooltip
                        content={series.tooltip}
                        trigger={<div><RegularText smaller content={series.name} /></div>}
                    />
                ),
                className: styles.name,
            },
            {
                key: `${series.name}-${series.code}-${series.year}-year`,
                content: <RegularText smaller content={series.year.toString()} />,
                className: styles.year,
            },
        ];
        return result.concat(
            series.dataset.map((data, idx) => {
                return {
                    key: `${series.name}-${series.code}-${series.year}-data-${idx}`,
                    content: <RegularText smaller content={formatValue(data, series.unitType)} />,
                    className: styles.value,
                };
            })
        );
    };

    const getHeaderItems = () => {
        const result: any[] = [];
        result.push({
            content: <TextTableHeader content="" />,
            key: `name`,
            className: styles.name,
        });
        result.push({
            content: <TextTableHeader content={pl.common.year} />,
            key: `year`,
            className: styles.year,
        });

        if (!props.reportData) {
            return result;
        }

        return result.concat(
            props.reportData.labels.map((label) => {
                return {
                    content: <TextTableHeader content={label} />,
                    key: label,
                    className: styles.value,
                };
            })
        );
    };

    const getRows = () => {
        if (!props.reportData) {
            return [];
        }

        return orderBy(props.reportData.seriesList, (s) => s.orderIndex).map((series, idx) => {
            return {
                key: `${series.code}_${series.year}_${idx}`,
                items: getRowsForSeries(series),
            };
        });
    };

    return (
        <div
            className={
                msTeamsContext.currentMsTeamsThemeName === IThemeTeamsClient.Default
                    ? styles.scrollbarDefaultTheme
                    : styles.scrollbarDarkTheme
            }
            ref={tableContainerRef}
            style={{
                width: "100%",
                overflowX: "auto",
                zIndex: 0,
            }}
            onTouchStart={() => {
                setShowHint(false);
            }}
        >
            <Flex
                hAlign="center"
                className={styles.swipeHint}
                style={{
                    opacity: showHint ? "unset" : "0",
                }}
                gap="gap.large"
            >
                <ArrowLeftIcon size="larger" />
                <HandIcon outline size="larger" />
                <ArrowRightIcon size="larger" />
            </Flex>
            <BasicTable rows={rows} headerItems={headerItems} />
        </div>
    );
};
